import type { ClerkOrgRole } from '@brew/auth/claims'
import {
  type BrandRole,
  type BrandRoleSnapshot,
  type Capability,
  can,
  type MemberBrandAccess,
  resolveEffectiveBrandRole,
} from '@brew/auth/rbac'

/**
 * Client-safe projection of one person's access inside the active org.
 *
 * Seeded by the app shell from the same Convex read that gates the brand
 * setup flow (`getBrandGateSnapshotForTenant`), then kept live by the
 * `memberRoles.queries.getMyOrgAccess` subscription. It deliberately lives
 * outside `lib/auth-org.ts` (`server-only`) so `OrgContextProvider` and the
 * `useCan` hooks can import it.
 */
export type OrgAccessSnapshot = Readonly<{
  orgRole: ClerkOrgRole
  /** How the person reaches brands; `all` for org admins (implicit everywhere). */
  brandAccess: MemberBrandAccess
  /** Per-brand grants by brand id. Empty unless `brandAccess` is `selected`. */
  brandRoles: Readonly<Record<string, BrandRole>>
}>

/** What the server seeds into `OrgContextProvider`. */
export type OrgContextSeed = Readonly<{
  userId: string
  orgId: string
  access: 'member' | 'admin'
  orgAccess: OrgAccessSnapshot
}>

export function isOrgAdminAccess(snapshot: OrgAccessSnapshot): boolean {
  return snapshot.orgRole === 'org:admin'
}

/**
 * Fail-closed seed for when the shell's snapshot read degraded: org admins
 * keep everything (implicit), org members get nothing until the live
 * subscription lands.
 */
export function emptyOrgAccess(access: 'member' | 'admin'): OrgAccessSnapshot {
  return {
    orgRole: access === 'admin' ? 'org:admin' : 'org:member',
    brandAccess: access === 'admin' ? 'all' : 'selected',
    brandRoles: {},
  }
}

/** The pure policy input the shared resolver reads, without I/O. */
export function toBrandRoleSnapshot(
  snapshot: OrgAccessSnapshot
): BrandRoleSnapshot {
  return {
    isOrgAdmin: isOrgAdminAccess(snapshot),
    everyBrand: snapshot.brandAccess === 'all',
    grants: Object.entries(snapshot.brandRoles).map(([brandId, role]) => ({
      brandId,
      role,
    })),
  }
}

/** Effective role on one brand (`null` brand = the org level). */
export function resolveBrandRoleFromSnapshot(
  snapshot: OrgAccessSnapshot,
  brandId: string | null
): BrandRole | null {
  return resolveEffectiveBrandRole({
    ...toBrandRoleSnapshot(snapshot),
    brandId,
  })
}

/** The capability answer for a snapshot on one brand (`null` brand = org level). */
export function canWithSnapshot(
  snapshot: OrgAccessSnapshot,
  brandId: string | null,
  capability: Capability
): boolean {
  return can(
    {
      isOrgAdmin: isOrgAdminAccess(snapshot),
      brandRole: resolveBrandRoleFromSnapshot(snapshot, brandId),
    },
    capability
  )
}

/** Keep only the brands the person may reach (org admins: all of them). */
export function filterAccessibleBrands<T extends { _id: string }>(
  brands: ReadonlyArray<T>,
  snapshot: OrgAccessSnapshot
): Array<T> {
  if (isOrgAdminAccess(snapshot)) {
    return [...brands]
  }
  return brands.filter(
    (brand) => resolveBrandRoleFromSnapshot(snapshot, brand._id) !== null
  )
}

/**
 * The ONE projection of a tenant brand-gate snapshot onto the access shape
 * every role resolver reads: the brand-access mode plus one role per listed
 * brand. Used by the shell seed, the API session role, and the server brand
 * context, so none of them re-derives precedence.
 */
export function orgAccessFromBrandGate(
  orgRole: OrgAccessSnapshot['orgRole'],
  gate: {
    brandAccess: MemberBrandAccess
    brands?: ReadonlyArray<{ _id: string; role: BrandRole }> | null
  }
): OrgAccessSnapshot {
  return {
    orgRole,
    brandAccess: gate.brandAccess,
    brandRoles: Object.fromEntries(
      (gate.brands ?? []).map((brand) => [brand._id, brand.role])
    ),
  }
}
